import {
  AgxButton,
  AgxFileUpload,
  AgxLabel,
  AgxMultiSelect,
  EntityImage,
  AgxColours,
} from '@urbanx/agx-ui-components'
import { StaffAction } from 'types/staff'
import { NotificationState } from 'types/commonTypes'
import { StaffRole } from '@urbanx/agx.auth.client'
import { States, UploadedDocument } from '@urbanx/agx.staff.client'
import { AgxSpacer, AgxTextInput } from '@urbanx/agx.ui-components2'

export interface StaffMemberFormValues {
  id?: string | undefined
  firstName?: string | undefined
  lastName?: string | undefined
  title?: string | undefined
  email?: string | undefined
  phoneNumber?: string | undefined
  emailInvitationSent?: boolean | undefined
  photo?: UploadedDocument | undefined
  hasLoggedIn?: boolean | undefined
  roles?: string[] | undefined
  states?: States[] | undefined
}

interface StaffMemberFormProps {
  pageAction: StaffAction.CREATE | StaffAction.EDIT
  staffMember: StaffMemberFormValues
  photoUri?: string
  isSaving: boolean
  isPhotoActionPending: boolean
  errorsList: string[]
  emailErrorText: string
  saveChangesActivated: boolean
  availableStaffRoleOptions: Array<{ label: string; value: string | StaffRole }>
  stateOptions: Array<{ value: States; label: string }>
  showAgencies: boolean
  agencyIds: string[]
  agenciesOptions: Array<{ label?: string; value?: string }>
  showEmailInvitation: boolean
  emailInviteState: NotificationState
  emailInviteDisabled: boolean
  onUpdateStaffMember: (updatedStaffMember: StaffMemberFormValues) => void
  onAgencyIdsChange: (value: string[]) => void
  onUploadPhoto: (file: File) => Promise<unknown>
  onRemovePhoto: () => void
  onSendInvite?: () => void
}

const rolesWithStateAccess = [
  StaffRole.ListingsContributor,
  StaffRole.LaunchContributor,
  StaffRole.ContractsContributor,
  StaffRole.TransactionsContributor,
  StaffRole.FinanceContributor,
].map((role) => role.toString())

const StaffMemberFormPage = ({
  pageAction,
  staffMember,
  photoUri,
  isSaving,
  isPhotoActionPending,
  errorsList,
  emailErrorText,
  saveChangesActivated,
  availableStaffRoleOptions,
  stateOptions,
  showAgencies,
  agencyIds,
  agenciesOptions,
  showEmailInvitation,
  emailInviteState,
  emailInviteDisabled,
  onUpdateStaffMember,
  onAgencyIdsChange,
  onUploadPhoto,
  onRemovePhoto,
  onSendInvite,
}: StaffMemberFormProps) => {
  const selectedRoles = staffMember.roles ?? []
  const selectedStates = staffMember.states?.map(String) ?? []
  const showStateSelect = selectedRoles.some((role) =>
    rolesWithStateAccess.includes(role)
  )

  return (
    <AgxSpacer
      direction="vertical"
      gap="spacing-5xl"
      className="container50Percent"
    >
      <AgxSpacer
        direction="horizontal"
        gap="spacing-xl"
        className="flexCentre"
        fill
      >
        <EntityImage
          addRingToImage
          backgroundColour={`var(--${AgxColours.NEUTRAL_GREY_500})`}
          photoUri={photoUri}
          size={72}
          usePlaceholderImage
        />
        <AgxSpacer
          direction="horizontal"
          gap="spacing-xl"
          contentAlignment="center"
        >
          <AgxFileUpload
            id="fileUpload"
            title=""
            buttonText={photoUri ? 'Change' : 'Upload'}
            isImageFileType
            disabled={isSaving || isPhotoActionPending}
            uploadFile={onUploadPhoto}
            extensions={['jpg', 'jpeg', 'png', 'svg', 'bmp']}
          />
          {photoUri && (
            <AgxButton
              text="Remove"
              medium
              hollow
              primary
              disabled={isSaving || isPhotoActionPending}
              onClick={onRemovePhoto}
            />
          )}
        </AgxSpacer>
      </AgxSpacer>

      <AgxSpacer direction="horizontal" gap="spacing-5xl">
        <AgxTextInput
          id="txtFirstName"
          type="text"
          label="First name"
          stretch
          optionalText=""
          defaultValue={staffMember.firstName}
          onInputValueChange={({ value }: { value: string }) =>
            onUpdateStaffMember({ ...staffMember, firstName: value })
          }
          errorText={
            errorsList.includes('firstName') ? 'First name required' : ''
          }
        />

        <AgxTextInput
          id="txtLastName"
          label="Last name"
          optionalText=""
          stretch
          defaultValue={staffMember.lastName}
          onInputValueChange={({ value }: { value: string }) =>
            onUpdateStaffMember({ ...staffMember, lastName: value })
          }
          errorText={
            errorsList.includes('lastName') ? 'Last name required' : ''
          }
          type="text"
        />
      </AgxSpacer>

      <AgxSpacer direction="horizontal">
        <AgxTextInput
          id="txtJobTitle"
          label="Job Title"
          optionalText=""
          stretch
          defaultValue={staffMember.title}
          onInputValueChange={({ value }: { value: string }) =>
            onUpdateStaffMember({ ...staffMember, title: value })
          }
          errorText={errorsList.includes('title') ? 'Job title required' : ''}
          type="text"
        />
      </AgxSpacer>

      <AgxSpacer direction="horizontal">
        <AgxTextInput
          id="txtEmail"
          label="Email"
          optionalText=""
          stretch
          disabled={pageAction === StaffAction.EDIT}
          defaultValue={staffMember.email}
          onInputValueChange={({ value }: { value: string }) =>
            onUpdateStaffMember({ ...staffMember, email: value })
          }
          type="email"
          errorText={errorsList.includes('email') ? emailErrorText : ''}
        />
      </AgxSpacer>

      <AgxSpacer direction="horizontal" gap="spacing-5xl">
        <AgxTextInput
          id="txtPhone"
          label="Phone"
          stretch
          defaultValue={staffMember.phoneNumber}
          onInputValueChange={({ value }: { value: string }) =>
            onUpdateStaffMember({ ...staffMember, phoneNumber: value })
          }
          type="phone"
          maxLength={15}
        />
      </AgxSpacer>

      <AgxSpacer direction="horizontal">
        <AgxMultiSelect
          id="SetRoles"
          label="Role(s)"
          options={availableStaffRoleOptions}
          defaultValue={selectedRoles}
          hideOptionalLabel
          parentControlValue
          onValueChanged={({ value }: { value: string[] }) => {
            onUpdateStaffMember({ ...staffMember, roles: value })
          }}
          required
          error={
            saveChangesActivated && selectedRoles.length === 0
              ? 'Role(s) required'
              : undefined
          }
        />
      </AgxSpacer>

      {showStateSelect && (
        <AgxSpacer direction="horizontal">
          <AgxMultiSelect
            id="SetStates"
            label="State(s)"
            options={stateOptions}
            defaultValue={selectedStates}
            hideOptionalLabel
            parentControlValue
            onValueChanged={({ value }: { value: string[] }) => {
              onUpdateStaffMember({ ...staffMember, states: value as States[] })
            }}
            error={
              selectedStates.length === 0 ? 'State(s) required' : undefined
            }
          />
        </AgxSpacer>
      )}

      {showAgencies && (
        <AgxSpacer direction="horizontal">
          <AgxMultiSelect
            id="selAgenciesForStaff"
            label="Agencies"
            hideOptionalLabel
            defaultValue={agencyIds}
            options={agenciesOptions}
            onValueChanged={({ value }: { value: string[] }) => {
              onAgencyIdsChange(value)
            }}
            parentControlValue
          />
        </AgxSpacer>
      )}

      {showEmailInvitation && (
        <AgxSpacer direction="horizontal" fill>
          <AgxSpacer direction="vertical" gap="spacing-md">
            <AgxLabel medium>Email Invitation</AgxLabel>
            <AgxButton
              text={
                emailInviteState !== NotificationState.Sending
                  ? `${
                      staffMember.emailInvitationSent ||
                      emailInviteState === NotificationState.Sent
                        ? 'Resend'
                        : 'Send'
                    } Invite`
                  : 'Sending...'
              }
              medium
              primary
              disabled={emailInviteDisabled}
              extraClasses={
                emailInviteState === NotificationState.Sending
                  ? 'emailInviteButton'
                  : ''
              }
              onClick={() => onSendInvite?.()}
            />
          </AgxSpacer>
        </AgxSpacer>
      )}
    </AgxSpacer>
  )
}

export default StaffMemberFormPage
